import { getDraggedSeriesId } from "@frontend/pages/case-workspace/lib/viewer-dnd"
import { useHangingProtocols } from "@frontend/pages/case-workspace/radiology/hooks/use-hanging-protocols"
import { useViewerSceneSeries } from "@frontend/pages/case-workspace/radiology/hooks/use-viewer-scene-series"
import { applyOverlapConstraint, buildOtherMasksUnion } from "@frontend/pages/case-workspace/radiology/lib/mask-overlap"
import { findStoredMaskByInstance } from "@frontend/pages/case-workspace/radiology/lib/mask-restore"
import { cmapper, NVImage } from "@niivue/niivue"
import { decodeRLE, encodeRLE } from "@niivue/niivue/drawing"
import { defaultLayouts, defaultSliceLayouts, useScene } from "@niivue/nvreact"
import { buildHangingProtocolAssignments, type HangingProtocol } from "@shared/lib/hanging-protocol"
import type { AnnotationSnapshotV1, MaskAnnotationState } from "@shared/lib/history-types"
import { createSingleLabelColormap, deriveInstanceColor } from "@shared/lib/mask-colormap"
import { useCallback, useEffect, useMemo, useRef } from "react"

import { buildMouseEventConfig } from "./mouse-mode"
import { buildProtocolActions } from "./protocol-actions"
import { useNvSam } from "./sam/use-nv-sam"
import type { MaskDrawingConfig, NvScenePanelProps } from "./types"
import { useNvSceneUiState } from "./ui-state"
import { sliceTypeOptions } from "./viewer-slice-layout-picker"

function maxSlots(layoutKey: string): number {
  return defaultLayouts[layoutKey]?.slots ?? 1
}

const DEFAULT_MASK_OPACITY = 0.7
// COLORMAP_TYPE.ZERO_TO_MAX_TRANSPARENT_BELOW_MIN is not exported at runtime by
// @niivue/niivue in this app's pinned version. NiiVue expects numeric colormap
// type values (0=min-max, 1=zero-max transparent below min, 2=translucent).
const MASK_COLORMAP_ZERO_TO_MAX_TRANSPARENT_BELOW_MIN = 1

function resolveMaskOpacity(maskId: string, opacityMap?: Record<string, number>): number {
  if (!opacityMap) return DEFAULT_MASK_OPACITY
  const pct = opacityMap[maskId]
  if (pct == null) return DEFAULT_MASK_OPACITY
  return Math.max(0, Math.min(1, pct / 100))
}

export function useNvScenePanelController({
  caseId,
  projectId,
  series,
  isSeriesDragging,
  masks,
  maskDrawing,
  snapshotToRestore,
  onSnapshotRestored,
  onActiveViewerSeriesChange,
  activeMaskLabelKey,
  onSamStateChange,
  getAuthHeaders,
  maskOpacityById
}: NvScenePanelProps) {
  const { scene, snapshot } = useScene()
  const {
    uiState,
    applyLayoutName,
    applyActiveViewerIndex,
    applyDropTargetIndex,
    applyViewerError,
    applyMouseMode,
    applySelectedProtocolId,
    applyGridOpen,
    applyCrosshairVisible,
    applyViewerSliceLayout
  } = useNvSceneUiState()
  const {
    layoutName,
    activeViewerIndex,
    dropTargetIndex,
    viewerError,
    mouseMode,
    selectedProtocolId,
    gridOpen,
    crosshairVisible,
    viewerSliceLayouts
  } = uiState
  type NiivueInstance = NonNullable<ReturnType<typeof scene.getNiivue>>
  const autoAppliedCaseRef = useRef<string | null>(null)
  const dropzoneElementsRef = useRef<Map<string, HTMLDivElement>>(new Map())
  const sliceLayoutPortalsRef = useRef<Map<string, HTMLDivElement>>(new Map())
  const sliceSliderPortalsRef = useRef<Map<string, HTMLDivElement>>(new Map())
  const lastMaskDrawingRef = useRef<MaskDrawingConfig | null>(null)
  const maskDrawingRef = useRef<MaskDrawingConfig | null>(null)
  const maskOpacityByIdRef = useRef<Record<string, number> | undefined>(maskOpacityById)
  // Cached union of all OTHER mask instances for overlap clipping, plus a
  // monotonic content version bumped whenever mask volumes are re-hydrated
  // (add/remove, active-instance switch, undo/redo, snapshot restore). The
  // cache key includes this version so a stroke never clips against stale
  // geometry, while repeated strokes within one drawing session reuse the
  // union (O(voxels) per stroke, independent of mask count).
  const overlapUnionCacheRef = useRef<{ key: string; union: Uint8Array | null } | null>(null)
  const maskContentVersionRef = useRef(0)
  // Tracks the last active { viewerIndex, annotationId } to detect instance switches
  const prevDrawStateRef = useRef<{
    viewerIndex: number
    annotationId: string
  } | null>(null)
  const loadSeriesToViewerRef =
    useRef<(viewerIndex: number, seriesId: string, options?: { activate?: boolean }) => Promise<void> | null>(null)

  const {
    protocols,
    isLoading: isLoadingProtocols,
    error: protocolError,
    createProtocol,
    updateProtocol,
    deleteProtocol
  } = useHangingProtocols(projectId)

  const { acquireVolumeForViewer, getAssignedSeriesId, releaseViewer, releaseViewersOutsideRange, clearAllViewers } =
    useViewerSceneSeries(caseId, series)
  const masksRef = useRef(masks)
  const emitActiveViewerSeriesChange = useCallback(
    (viewerIndex: number) => {
      onActiveViewerSeriesChange?.(getAssignedSeriesId(viewerIndex) ?? null)
    },
    [getAssignedSeriesId, onActiveViewerSeriesChange]
  )

  const handleViewerSliceLayoutChange = useCallback(
    (viewerIndex: number, layoutKey: string | null) => {
      const sliceTypeOption = layoutKey ? sliceTypeOptions[layoutKey] : null
      if (sliceTypeOption) {
        scene.setViewerSliceLayout(viewerIndex, null)
        scene.getNiivue(viewerIndex)?.setSliceType(sliceTypeOption.sliceType)
      } else {
        const layout = layoutKey ? (defaultSliceLayouts[layoutKey]?.layout ?? null) : null
        scene.setViewerSliceLayout(viewerIndex, layout)
      }
      applyViewerSliceLayout(viewerIndex, layoutKey)
    },
    [scene, applyViewerSliceLayout]
  )

  const selectedProtocol = useMemo(
    () => protocols.find((item) => item.id === selectedProtocolId) ?? null,
    [protocols, selectedProtocolId]
  )

  const ensureLayoutViewerCount = useCallback(
    (nextLayout: string) => {
      const targetSlots = maxSlots(nextLayout)
      if (scene.viewers.length > targetSlots) {
        for (let i = scene.viewers.length - 1; i >= targetSlots; i -= 1) {
          releaseViewer(i)
          scene.removeViewer(i)
        }
      }
      while (scene.viewers.length < targetSlots && scene.canAddViewer()) {
        scene.addViewer({ loadingText: "VIRDX" })
      }
    },
    [releaseViewer, scene]
  )

  const clearViewerVolumes = useCallback(
    (viewerIndex: number) => {
      const nv = scene.getNiivue(viewerIndex)
      if (nv) {
        const volumes = [...nv.volumes]
        for (const volume of volumes) {
          nv.removeVolume(volume)
        }
      }
      releaseViewer(viewerIndex)
      if (prevDrawStateRef.current?.viewerIndex === viewerIndex) {
        prevDrawStateRef.current = null
      }
    },
    [releaseViewer, scene]
  )

  const normalizeMaskImage = useCallback((volume: NVImage, expectedLength: number): Uint8Array => {
    if (volume.img instanceof Uint8Array && volume.img.length === expectedLength) {
      return volume.img
    }

    const next = new Uint8Array(expectedLength)
    const src = volume.img
    if (src) {
      const copyLength = Math.min(src.length, expectedLength)
      for (let i = 0; i < copyLength; i += 1) {
        next[i] = src[i] ?? 0
      }
    }
    volume.img = next
    return next
  }, [])

  const applyDrawLutForLabel = useCallback((nv: NiivueInstance, label: { color: string }) => {
    nv.drawLut = cmapper.makeDrawLut(createSingleLabelColormap(label.color))
  }, [])

  type MaskRenderTarget = {
    mask: MaskAnnotationState
    color: string
  }

  const getMaskTargetsForSeries = useCallback(
    (params: {
      sourceMasks: MaskAnnotationState[]
      maskConfig: MaskDrawingConfig
      seriesId: string
    }): MaskRenderTarget[] => {
      const { sourceMasks, maskConfig, seriesId } = params
      if (!maskConfig.projectModuleId) return []
      const colorByLabelKey = new Map(maskConfig.labels.map((label) => [label.key, label.color]))

      return sourceMasks
        .filter((mask) => {
          if (mask.projectModuleId !== maskConfig.projectModuleId) return false
          if (mask.seriesId !== seriesId) return false
          if (!mask.labelName) return false
          return colorByLabelKey.has(mask.labelName)
        })
        .sort((a, b) => {
          const aValue = a.labelValue ?? Number.MAX_SAFE_INTEGER
          const bValue = b.labelValue ?? Number.MAX_SAFE_INTEGER
          if (aValue !== bValue) return aValue - bValue
          if (a.instanceOrdinal !== b.instanceOrdinal) {
            return a.instanceOrdinal - b.instanceOrdinal
          }
          return a.id.localeCompare(b.id)
        })
        .map((mask) => ({
          mask,
          color: deriveInstanceColor(colorByLabelKey.get(mask.labelName!)!, mask.instanceOrdinal)
        }))
    },
    []
  )

  const ensureMaskVolumes = useCallback((nv: NiivueInstance, targets: MaskRenderTarget[]) => {
    const background = nv.volumes[0]
    if (!background) return false

    const requiredVolumeCount = targets.length + 1

    while (nv.volumes.length > requiredVolumeCount) {
      const tail = nv.volumes[nv.volumes.length - 1]
      if (!tail) break
      nv.removeVolume(tail)
    }

    while (nv.volumes.length < requiredVolumeCount) {
      const maskVolume = NVImage.zerosLike(background, "uint8")
      maskVolume.opacity = DEFAULT_MASK_OPACITY
      nv.addVolume(maskVolume)
    }

    const currentOpacityMap = maskOpacityByIdRef.current
    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i]!
      const maskVolume = nv.volumes[i + 1]
      if (!maskVolume) continue
      maskVolume.name = `mask:${target.mask.id}`
      maskVolume.opacity = resolveMaskOpacity(target.mask.id, currentOpacityMap)
      // Guardrail: make zero-valued mask background fully transparent.
      maskVolume.colormapType = MASK_COLORMAP_ZERO_TO_MAX_TRANSPARENT_BELOW_MIN
      maskVolume.cal_min = 0.5
      maskVolume.cal_max = 1.5
      maskVolume.setColormapLabel(createSingleLabelColormap(target.color))
    }

    return true
  }, [])

  const decodeStoredMaskBitmap = useCallback((stored: Uint8Array, expectedLength: number): Uint8Array => {
    if (stored.length === 0) return new Uint8Array(expectedLength)
    return decodeRLE(stored, expectedLength)
  }, [])

  const hydrateViewerMasks = useCallback(
    (params: {
      viewerIndex: number
      seriesId: string
      sourceMasks: MaskAnnotationState[]
      maskConfig: MaskDrawingConfig
      syncActiveMaskInstance?: boolean
    }) => {
      const { viewerIndex, seriesId, sourceMasks, maskConfig, syncActiveMaskInstance = false } = params
      const nv = scene.getNiivue(viewerIndex)
      if (!nv?.volumes[0]) return

      const { activeMaskInstanceId, projectModuleId } = maskConfig
      if (!projectModuleId) return

      // Mask volume contents are about to be rewritten from stored RLE; bump the
      // content version so any cached overlap union is treated as stale.
      maskContentVersionRef.current += 1
      const targets = getMaskTargetsForSeries({
        sourceMasks,
        maskConfig,
        seriesId
      })
      if (!ensureMaskVolumes(nv, targets)) return

      const firstMaskVolume = nv.volumes[1]
      const expectedLength =
        (firstMaskVolume?.img ? firstMaskVolume.img.length : undefined) ??
        (nv.volumes[0]?.img ? nv.volumes[0].img.length : undefined)
      if (!expectedLength) return

      for (let i = 0; i < targets.length; i += 1) {
        const target = targets[i]!
        const volume = nv.volumes[i + 1]
        if (!volume) continue

        const volumeImg = normalizeMaskImage(volume, expectedLength)
        volumeImg.fill(0)

        const storedMask = findStoredMaskByInstance({
          masks: sourceMasks,
          annotationId: target.mask.id,
          projectModuleId,
          seriesId
        })
        if (!storedMask) continue

        const restoredBitmap = decodeStoredMaskBitmap(storedMask.rle, expectedLength)
        volumeImg.set(restoredBitmap.subarray(0, expectedLength))
      }

      // Push hydrated mask buffers to GPU so overlays render immediately
      // without requiring a viewer focus change.
      nv.updateGLVolume()

      if (syncActiveMaskInstance && activeMaskInstanceId) {
        const activeInstanceIndex = targets.findIndex((target) => target.mask.id === activeMaskInstanceId)
        if (activeInstanceIndex >= 0) {
          const activeVolume = nv.volumes[activeInstanceIndex + 1]
          if (activeVolume) {
            nv.drawBitmap = normalizeMaskImage(activeVolume, expectedLength)
          }
        }
        prevDrawStateRef.current = {
          viewerIndex,
          annotationId: activeMaskInstanceId
        }
      } else if (syncActiveMaskInstance) {
        // Active instance was deleted or not found in targets — clear the stale
        // drawBitmap so refreshDrawing doesn't render ghost data with the old LUT.
        if (nv.drawBitmap) {
          nv.drawBitmap.fill(0)
        }
        nv.setDrawingEnabled(false)
        if (prevDrawStateRef.current?.viewerIndex === viewerIndex) {
          prevDrawStateRef.current = null
        }
      }

      nv.refreshDrawing(true)
    },
    [decodeStoredMaskBitmap, ensureMaskVolumes, getMaskTargetsForSeries, normalizeMaskImage, scene]
  )

  const commitMaskInstanceState = useCallback(
    (params: {
      nv: NiivueInstance
      viewerIndex: number
      annotationId: string
      maskConfig: MaskDrawingConfig
      onBitmapCommit?: MaskDrawingConfig["onBitmapCommit"]
    }) => {
      const { nv, viewerIndex, annotationId, maskConfig, onBitmapCommit } = params

      const drawBitmap = nv.drawBitmap
      if (!drawBitmap) return

      const seriesId = getAssignedSeriesId(viewerIndex)
      if (!seriesId) return
      const targets = getMaskTargetsForSeries({
        sourceMasks: masksRef.current,
        maskConfig,
        seriesId
      })
      const targetIndex = targets.findIndex((target) => target.mask.id === annotationId)
      if (targetIndex < 0) return

      const target = targets[targetIndex]!
      if (!target.mask.labelName) return
      const maskVolume = nv.volumes[targetIndex + 1]
      if (!maskVolume) return

      // Enforce one-label-per-voxel when the module disallows overlap. Clip the
      // active mask's freshly drawn voxels against the cached union of all other
      // mask instances before persisting/rendering. The union is keyed by
      // (viewer, active instance, mask-content version) so repeated strokes cost
      // O(voxels) and never rescan every mask, yet invalidate on re-hydration.
      let clippedOverlap = false
      if (maskConfig.disallowOverlap) {
        const cacheKey = `${viewerIndex}|${annotationId}|${maskContentVersionRef.current}`
        let cache = overlapUnionCacheRef.current
        if (!cache || cache.key !== cacheKey) {
          cache = { key: cacheKey, union: buildOtherMasksUnion(nv, annotationId) }
          overlapUnionCacheRef.current = cache
        }
        clippedOverlap = applyOverlapConstraint(drawBitmap, cache.union) > 0
      }

      const maskImg = normalizeMaskImage(maskVolume, drawBitmap.length)
      maskImg.set(drawBitmap)
      nv.updateGLVolume()
      // If voxels were removed from the active draw layer, force a redraw so the
      // clipped voxels disappear immediately (the active mask renders from
      // drawBitmap, not the committed volume).
      if (clippedOverlap) {
        nv.refreshDrawing(true)
      }

      onBitmapCommit?.({
        annotationId,
        seriesId,
        labelKey: target.mask.labelName,
        rle: encodeRLE(maskImg)
      })
    },
    [getAssignedSeriesId, getMaskTargetsForSeries, normalizeMaskImage]
  )

  const loadSeriesToViewer = useCallback(
    async (viewerIndex: number, seriesId: string, options?: { activate?: boolean }) => {
      const viewer = scene.viewers[viewerIndex]
      if (!viewer) return
      applyViewerError(null)

      try {
        clearViewerVolumes(viewerIndex)
        const volume = await acquireVolumeForViewer(viewerIndex, seriesId)
        const nv = scene.getNiivue(viewerIndex)
        if (!nv) {
          throw new Error("Viewer instance is not available")
        }
        const image = await NVImage.loadFromUrl({
          url: volume.url,
          name: volume.name
        })
        nv.addVolume(image)
        if (maskDrawing?.projectModuleId) {
          hydrateViewerMasks({
            viewerIndex,
            seriesId,
            sourceMasks: masksRef.current,
            maskConfig: maskDrawing,
            syncActiveMaskInstance:
              Boolean(maskDrawing.activeMaskInstanceId) &&
              (options?.activate !== false || viewerIndex === activeViewerIndex)
          })
        }
        if (viewerIndex === activeViewerIndex) {
          emitActiveViewerSeriesChange(viewerIndex)
        }
        if (options?.activate !== false) {
          applyActiveViewerIndex(viewerIndex)
          emitActiveViewerSeriesChange(viewerIndex)
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to load series"
        applyViewerError(message)
      }
    },
    [
      activeViewerIndex,
      acquireVolumeForViewer,
      clearViewerVolumes,
      emitActiveViewerSeriesChange,
      hydrateViewerMasks,
      maskDrawing,
      scene,
      applyActiveViewerIndex,
      applyViewerError
    ]
  )
  useEffect(() => {
    loadSeriesToViewerRef.current = loadSeriesToViewer
  }, [loadSeriesToViewer])

  const applyProtocol = useCallback(
    async (protocol: HangingProtocol) => {
      applyViewerError(null)
      applyLayoutName(protocol.layout_key)
      scene.setLayout(protocol.layout_key)
      ensureLayoutViewerCount(protocol.layout_key)

      for (let i = 0; i < scene.viewers.length; i += 1) {
        clearViewerVolumes(i)
      }

      const assignments = buildHangingProtocolAssignments(protocol, series)
      for (const assignment of assignments) {
        if (assignment.viewerIndex >= scene.viewers.length) continue
        await loadSeriesToViewer(assignment.viewerIndex, assignment.seriesId, {
          activate: false
        })
      }
      applyActiveViewerIndex(0)
      emitActiveViewerSeriesChange(0)
    },
    [
      clearViewerVolumes,
      emitActiveViewerSeriesChange,
      ensureLayoutViewerCount,
      loadSeriesToViewer,
      scene,
      series,
      applyActiveViewerIndex,
      applyLayoutName,
      applyViewerError
    ]
  )

  useEffect(() => {
    if (snapshot.viewerCount > 0) return
    scene.setLayout(layoutName)
  }, [layoutName, scene, snapshot.viewerCount])

  useEffect(() => {
    if (snapshot.viewerCount === 0) return
    ensureLayoutViewerCount(layoutName)
  }, [ensureLayoutViewerCount, layoutName, snapshot.viewerCount])

  useEffect(() => {
    if (activeViewerIndex >= snapshot.viewerCount) {
      applyActiveViewerIndex(Math.max(0, snapshot.viewerCount - 1))
    }
    releaseViewersOutsideRange(snapshot.viewerCount)
  }, [activeViewerIndex, releaseViewersOutsideRange, snapshot.viewerCount, applyActiveViewerIndex])

  useEffect(() => {
    const config = buildMouseEventConfig(mouseMode)
    const isSam = maskDrawing?.samMode === true
    for (let viewerIndex = 0; viewerIndex < snapshot.viewerCount; viewerIndex += 1) {
      // Skip active viewer when SAM mode is active — the SAM hook manages its own mouse config
      if (isSam && viewerIndex === activeViewerIndex) continue
      scene.getNiivue(viewerIndex)?.setMouseEventConfig(config)
    }
  }, [mouseMode, scene, snapshot.viewerCount, maskDrawing?.samMode, activeViewerIndex])

  // Sync crosshair visibility and default width unit across all viewers
  useEffect(() => {
    for (let viewerIndex = 0; viewerIndex < snapshot.viewerCount; viewerIndex += 1) {
      const nv = scene.getNiivue(viewerIndex)
      if (!nv) continue
      nv.opts.crosshairWidthUnit = "percent"
      nv.setCrosshairWidth(crosshairVisible ? 0.5 : 0)
    }
  }, [crosshairVisible, scene, snapshot.viewerCount])

  useEffect(() => {
    emitActiveViewerSeriesChange(activeViewerIndex)
  }, [activeViewerIndex, emitActiveViewerSeriesChange])

  // Every listener registered below is pushed onto `cleanups`, which the returned
  // function drains; react-doctor cannot follow the indirection.
  // react-doctor-disable-next-line react-doctor/effect-needs-cleanup
  useEffect(() => {
    const cleanups: Array<() => void> = []
    const liveViewerIds = new Set<string>()

    scene.viewers.forEach((viewer, index) => {
      liveViewerIds.add(viewer.id)
      viewer.niivue.opts.loadingText = "VIRDX"

      const handleClick = () => applyActiveViewerIndex(index)
      viewer.containerDiv.addEventListener("click", handleClick)
      cleanups.push(() => viewer.containerDiv.removeEventListener("click", handleClick))

      let overlay = dropzoneElementsRef.current.get(viewer.id)
      if (!overlay) {
        overlay = document.createElement("div")
        overlay.className = "vxa-viewer-dropzone"
        Object.assign(overlay.style, {
          position: "absolute",
          inset: "0",
          zIndex: "20",
          pointerEvents: "none",
          background: "transparent"
        })
        viewer.containerDiv.appendChild(overlay)
        dropzoneElementsRef.current.set(viewer.id, overlay)
      }

      if (!sliceLayoutPortalsRef.current.has(viewer.id)) {
        const portalEl = document.createElement("div")
        portalEl.className = "vxa-slice-layout-portal"
        Object.assign(portalEl.style, {
          position: "absolute",
          top: "4px",
          left: "4px",
          right: "4px",
          zIndex: "30",
          pointerEvents: "none"
        })
        viewer.containerDiv.appendChild(portalEl)
        sliceLayoutPortalsRef.current.set(viewer.id, portalEl)
      }

      if (!sliceSliderPortalsRef.current.has(viewer.id)) {
        const sliderEl = document.createElement("div")
        sliderEl.className = "vxa-slice-slider-portal"
        Object.assign(sliderEl.style, {
          position: "absolute",
          top: "15%",
          right: "4px",
          bottom: "15%",
          width: "24px",
          zIndex: "30",
          pointerEvents: "none",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "4px"
        })
        viewer.containerDiv.appendChild(sliderEl)
        sliceSliderPortalsRef.current.set(viewer.id, sliderEl)
      }

      const resolveIndex = () => scene.viewers.findIndex((slot) => slot.id === viewer.id)

      const handleDragEnter = (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        const viewerIndex = resolveIndex()
        if (viewerIndex >= 0) {
          applyDropTargetIndex(viewerIndex)
        }
      }
      const handleDragOver = (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        if (event.dataTransfer) {
          event.dataTransfer.dropEffect = "copy"
        }
        const viewerIndex = resolveIndex()
        if (viewerIndex >= 0) {
          applyDropTargetIndex(viewerIndex)
        }
      }
      const handleDragLeave = (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        const currentTarget = event.currentTarget as HTMLElement | null
        const nextTarget = event.relatedTarget as Node | null
        if (!currentTarget || (nextTarget && currentTarget.contains(nextTarget))) return
        const viewerIndex = resolveIndex()
        if (viewerIndex >= 0) {
          applyDropTargetIndex(null)
        }
      }
      const handleDrop = (event: DragEvent) => {
        event.preventDefault()
        event.stopPropagation()
        const viewerIndex = resolveIndex()
        applyDropTargetIndex(null)
        if (viewerIndex < 0 || !event.dataTransfer) return
        const seriesId = getDraggedSeriesId(event.dataTransfer)
        if (!seriesId) return
        const loader = loadSeriesToViewerRef.current
        if (!loader) return
        void loader(viewerIndex, seriesId)
      }

      overlay.addEventListener("dragenter", handleDragEnter)
      overlay.addEventListener("dragover", handleDragOver)
      overlay.addEventListener("dragleave", handleDragLeave)
      overlay.addEventListener("drop", handleDrop)
      cleanups.push(() => {
        overlay?.removeEventListener("dragenter", handleDragEnter)
        overlay?.removeEventListener("dragover", handleDragOver)
        overlay?.removeEventListener("dragleave", handleDragLeave)
        overlay?.removeEventListener("drop", handleDrop)
      })

      overlay.style.pointerEvents = isSeriesDragging ? "auto" : "none"
    })

    for (const [viewerId, overlay] of dropzoneElementsRef.current) {
      if (liveViewerIds.has(viewerId)) continue
      overlay.remove()
      dropzoneElementsRef.current.delete(viewerId)
    }

    for (const [viewerId, portalEl] of sliceLayoutPortalsRef.current) {
      if (liveViewerIds.has(viewerId)) continue
      portalEl.remove()
      sliceLayoutPortalsRef.current.delete(viewerId)
    }

    for (const [viewerId, sliderEl] of sliceSliderPortalsRef.current) {
      if (liveViewerIds.has(viewerId)) continue
      sliderEl.remove()
      sliceSliderPortalsRef.current.delete(viewerId)
    }

    return () => {
      for (const cleanup of cleanups) {
        cleanup()
      }
    }
  }, [isSeriesDragging, scene, snapshot.viewerCount, applyActiveViewerIndex, applyDropTargetIndex])

  useEffect(() => {
    scene.viewers.forEach((viewer, index) => {
      const _isActive = index === activeViewerIndex
      const _isDropTarget = index === dropTargetIndex
      viewer.containerDiv.style.borderRadius = "8px"
      viewer.containerDiv.style.overflow = "hidden"
    })
  }, [activeViewerIndex, dropTargetIndex, scene])

  // If the selected protocol was deleted (no longer in the list), reset to null.
  // A null selectedProtocolId is a valid user choice ("None / new"), so we
  // must not force-select the first protocol when the user explicitly deselects.
  useEffect(() => {
    if (selectedProtocolId && protocols.length > 0 && !protocols.some((item) => item.id === selectedProtocolId)) {
      applySelectedProtocolId(null)
    }
  }, [protocols, selectedProtocolId, applySelectedProtocolId])

  useEffect(() => {
    autoAppliedCaseRef.current = null
    applySelectedProtocolId(null)
    for (let i = 0; i < scene.viewers.length; i += 1) {
      clearViewerVolumes(i)
    }
    clearAllViewers()
  }, [clearAllViewers, clearViewerVolumes, scene, applySelectedProtocolId])

  useEffect(() => {
    if (snapshot.viewerCount === 0) return
    if (isLoadingProtocols) return
    if (series.length === 0) return
    if (autoAppliedCaseRef.current === caseId) return

    const defaultProtocol = protocols.find((item) => item.is_default)
    if (defaultProtocol) {
      applySelectedProtocolId(defaultProtocol.id)
      void applyProtocol(defaultProtocol)
    }
    autoAppliedCaseRef.current = caseId
  }, [
    applyProtocol,
    caseId,
    isLoadingProtocols,
    protocols,
    series.length,
    snapshot.viewerCount,
    applySelectedProtocolId
  ])

  useEffect(() => {
    masksRef.current = masks
  }, [masks])

  useEffect(() => {
    maskDrawingRef.current = maskDrawing ?? null
    if (!maskDrawing) return
    lastMaskDrawingRef.current = maskDrawing
  }, [maskDrawing])

  // Keep opacity ref in sync and apply opacity-only updates without
  // re-running the heavy drawing-setup effect.
  useEffect(() => {
    maskOpacityByIdRef.current = maskOpacityById
  }, [maskOpacityById])

  useEffect(() => {
    const currentMaskDrawing = maskDrawingRef.current
    if (!currentMaskDrawing?.activeMaskInstanceId) return
    if (!maskOpacityById) return

    const nv = scene.getNiivue(activeViewerIndex)
    if (!nv?.volumes[0]) return

    const seriesId = getAssignedSeriesId(activeViewerIndex)
    if (!seriesId) return

    const targets = getMaskTargetsForSeries({
      sourceMasks: masksRef.current,
      maskConfig: currentMaskDrawing,
      seriesId
    })
    const activeTargetIndex = targets.findIndex((t) => t.mask.id === currentMaskDrawing.activeMaskInstanceId)

    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i]!
      const maskVolume = nv.volumes[i + 1]
      if (!maskVolume) continue
      maskVolume.opacity = i === activeTargetIndex ? 0 : resolveMaskOpacity(target.mask.id, maskOpacityById)
    }
    if (currentMaskDrawing.activeMaskInstanceId) {
      nv.setDrawOpacity(resolveMaskOpacity(currentMaskDrawing.activeMaskInstanceId, maskOpacityById))
    }
    nv.updateGLVolume()
  }, [activeViewerIndex, getAssignedSeriesId, getMaskTargetsForSeries, maskOpacityById, scene])

  // SAM integration
  const buildRestoreMouseConfig = useCallback(() => buildMouseEventConfig(mouseMode), [mouseMode])
  const { samAvailable, samPending, samError, handleScribbleCommit } = useNvSam({
    scene,
    activeViewerIndex,
    maskDrawing: maskDrawing ?? null,
    samMode: maskDrawing?.samMode ?? false,
    samScribbleMode: maskDrawing?.samScribbleMode ?? false,
    buildRestoreMouseConfig,
    getAssignedSeriesId: (viewerIndex: number) => getAssignedSeriesId(viewerIndex) ?? null,
    activeMaskLabelKey: activeMaskLabelKey ?? null,
    getAuthHeaders: getAuthHeaders ?? (async () => undefined),
    samConfidence: maskDrawing?.samConfidence ?? 0.5
  })

  const prevSamStateRef = useRef<{ samAvailable: boolean; samPending: boolean; samError: string | null } | null>(null)
  useEffect(() => {
    const prev = prevSamStateRef.current
    if (prev && prev.samAvailable === samAvailable && prev.samPending === samPending && prev.samError === samError)
      return
    const next = { samAvailable, samPending, samError }
    prevSamStateRef.current = next
    onSamStateChange?.(next)
  }, [samAvailable, samPending, samError, onSamStateChange])

  useEffect(() => {
    const nv = scene.getNiivue(activeViewerIndex)
    if (!nv) return

    if (!maskDrawing?.activeMaskInstanceId) {
      const prev = prevDrawStateRef.current
      const previousConfig = lastMaskDrawingRef.current

      // 1. Commit outgoing instance state while drawBitmap still holds data.
      if (prev && previousConfig && prev.viewerIndex === activeViewerIndex) {
        commitMaskInstanceState({
          nv,
          viewerIndex: activeViewerIndex,
          annotationId: prev.annotationId,
          maskConfig: previousConfig,
          onBitmapCommit: previousConfig.onBitmapCommit
        })
      }

      // 2. Detach drawBitmap by replacing it with a standalone zeroed buffer.
      //    Previously drawBitmap was aliased to the active mask volume's .img.
      //    When ensureMaskVolumes repurposes that volume for a different mask,
      //    the hydration loop writes the new mask's pixel data into the SAME
      //    buffer — causing a ghost overlay with the deleted mask's color LUT.
      //    Allocating a fresh buffer breaks the alias and prevents this.
      const drawLen = nv.drawBitmap?.length ?? 0
      if (drawLen > 0) {
        nv.drawBitmap = new Uint8Array(drawLen)
      }
      nv.setDrawingEnabled(false)
      nv.refreshDrawing(true)

      // 3. Restore volume count and opacity for remaining masks so they
      //    stay visible after drawing is deactivated.
      if (previousConfig) {
        const seriesId = getAssignedSeriesId(activeViewerIndex)
        if (seriesId) {
          const targets = getMaskTargetsForSeries({
            sourceMasks: masksRef.current,
            maskConfig: previousConfig,
            seriesId
          })
          if (ensureMaskVolumes(nv, targets)) {
            const opMap = maskOpacityByIdRef.current
            for (let i = 0; i < targets.length; i += 1) {
              const target = targets[i]!
              const maskVolume = nv.volumes[i + 1]
              if (!maskVolume) continue
              maskVolume.opacity = resolveMaskOpacity(target.mask.id, opMap)
            }
            nv.setDrawOpacity(DEFAULT_MASK_OPACITY)
            nv.updateGLVolume()
          }
        }
      }

      prevDrawStateRef.current = null
      return
    }

    if (!nv.volumes[0]) return

    const { activeMaskInstanceId, brushSize, filledPen, eraseMode } = maskDrawing
    const seriesId = getAssignedSeriesId(activeViewerIndex)
    if (!seriesId) return
    const targets = getMaskTargetsForSeries({
      sourceMasks: masksRef.current,
      maskConfig: maskDrawing,
      seriesId
    })
    const activeTargetIndex = targets.findIndex((target) => target.mask.id === activeMaskInstanceId)
    if (activeTargetIndex < 0) return
    const activeTarget = targets[activeTargetIndex]!
    if (!ensureMaskVolumes(nv, targets)) return
    const opMap = maskOpacityByIdRef.current
    for (let i = 0; i < targets.length; i += 1) {
      const target = targets[i]!
      const maskVolume = nv.volumes[i + 1]
      if (!maskVolume) continue
      // Active drawing instance: volume hidden (drawBitmap renders it);
      // setDrawOpacity controls its visual opacity.
      maskVolume.opacity = i === activeTargetIndex ? 0 : resolveMaskOpacity(target.mask.id, opMap)
    }
    nv.setDrawOpacity(resolveMaskOpacity(activeMaskInstanceId, opMap))
    nv.updateGLVolume()

    // In SAM mode, we still need to bind drawBitmap to the active mask volume
    // so the SAM hook can write segmentation results into it, but we don't enable
    // NiiVue's brush drawing (the SAM hook manages its own mouse config).
    const isSamMode = maskDrawing.samMode === true && !maskDrawing.samScribbleMode
    const isDrawingDisabled = maskDrawing.drawingDisabled === true

    // Enable drawing and apply label LUT. This exact sequence must be preserved:
    // enable -> apply draw LUT -> refresh colormaps.
    // Regression note: skipping refreshColormaps causes the first stroke to render
    // with NiiVue's default red before the active label LUT is uploaded.
    const initDrawing = () => {
      nv.setDrawingEnabled(true)
      applyDrawLutForLabel(nv, { color: activeTarget.color })
      nv.refreshColormaps()
    }

    if (isDrawingDisabled) {
      // Drawing tools toggled off: allocate drawBitmap and apply LUT so the
      // active overlay stays visible, but don't enable brush input.
      initDrawing()
      nv.setDrawingEnabled(false)
    } else if (!isSamMode) {
      initDrawing()
    } else {
      // SAM mode: ensure drawBitmap is allocated and LUT matches the active label.
      // Always apply the LUT so label color switches are reflected immediately.
      initDrawing()
      nv.setDrawingEnabled(false)
    }

    const expectedLength = nv.drawBitmap?.length
    if (!expectedLength) return

    // Detect instance or viewer change
    const prev = prevDrawStateRef.current
    const activeInstanceChanged =
      !prev || prev.viewerIndex !== activeViewerIndex || prev.annotationId !== activeMaskInstanceId

    if (activeInstanceChanged) {
      // Persist outgoing instance state before rebinding drawBitmap.
      if (prev) {
        const prevNv = scene.getNiivue(prev.viewerIndex)
        if (prevNv) {
          const prevConfig = prev.viewerIndex === activeViewerIndex ? maskDrawing : lastMaskDrawingRef.current
          if (prevConfig) {
            commitMaskInstanceState({
              nv: prevNv,
              viewerIndex: prev.viewerIndex,
              annotationId: prev.annotationId,
              maskConfig: prevConfig,
              onBitmapCommit: prevConfig.onBitmapCommit
            })
          }
        }
      }

      const incomingVolume = nv.volumes[activeTargetIndex + 1]
      if (!incomingVolume) return
      const incomingBitmap = normalizeMaskImage(incomingVolume, expectedLength)
      nv.drawBitmap = incomingBitmap

      nv.refreshDrawing(true)
      prevDrawStateRef.current = {
        viewerIndex: activeViewerIndex,
        annotationId: activeMaskInstanceId
      }
    }

    if (!(isSamMode || isDrawingDisabled)) {
      nv.setPenValue(eraseMode ? 0 : 1, eraseMode ? false : filledPen)
      nv.opts.penSize = brushSize
    }
  }, [
    activeViewerIndex,
    commitMaskInstanceState,
    ensureMaskVolumes,
    applyDrawLutForLabel,
    getAssignedSeriesId,
    getMaskTargetsForSeries,
    maskDrawing,
    normalizeMaskImage,
    scene
  ])

  useEffect(() => {
    const cleanups: Array<() => void> = []

    for (let viewerIndex = 0; viewerIndex < snapshot.viewerCount; viewerIndex += 1) {
      const nv = scene.getNiivue(viewerIndex)
      if (!nv) continue

      const previousOnMouseUp = nv.onMouseUp
      const commitOnMouseUp = (data: Parameters<typeof previousOnMouseUp>[0]) => {
        previousOnMouseUp(data)

        const currentMaskDrawing = maskDrawingRef.current
        if (!currentMaskDrawing?.activeMaskInstanceId) return
        if (viewerIndex !== activeViewerIndex) return

        // SAM scribble mode: intercept the commit and send to SAM instead
        if (currentMaskDrawing.samScribbleMode) {
          handleScribbleCommit(nv, viewerIndex)
          return
        }

        commitMaskInstanceState({
          nv,
          viewerIndex,
          annotationId: currentMaskDrawing.activeMaskInstanceId,
          maskConfig: currentMaskDrawing,
          onBitmapCommit: currentMaskDrawing.onBitmapCommit
        })
      }

      nv.onMouseUp = commitOnMouseUp
      cleanups.push(() => {
        if (nv.onMouseUp === commitOnMouseUp) {
          nv.onMouseUp = previousOnMouseUp
        }
      })
    }

    return () => {
      for (const cleanup of cleanups) {
        cleanup()
      }
    }
  }, [activeViewerIndex, commitMaskInstanceState, handleScribbleCommit, scene, snapshot.viewerCount])

  // Stable fingerprint of mask IDs — changes only when masks are added/removed,
  // not on every RLE update (brush stroke) or storageState change (auto-save).
  const maskIdsFingerprint = useMemo(
    () =>
      masks
        .map((m) => m.id)
        .sort()
        .join(","),
    [masks]
  )

  const overlayScopeKey = useMemo(() => {
    if (!maskDrawing?.projectModuleId) return null
    const labelsKey = maskDrawing.labels.map((label) => `${label.key}:${label.color}`).join("|")
    return `${maskDrawing.projectModuleId}|${labelsKey}|${maskDrawing.activeMaskInstanceId ?? "none"}|${maskIdsFingerprint}`
  }, [maskDrawing?.activeMaskInstanceId, maskDrawing?.labels, maskDrawing?.projectModuleId, maskIdsFingerprint])

  const processedSnapshotRef = useRef<AnnotationSnapshotV1 | null>(null)

  useEffect(() => {
    if (!overlayScopeKey) return
    const currentMaskDrawing = maskDrawingRef.current
    if (!currentMaskDrawing?.projectModuleId) return

    for (let i = 0; i < snapshot.viewerCount; i += 1) {
      const seriesId = getAssignedSeriesId(i)
      if (!seriesId) continue
      hydrateViewerMasks({
        viewerIndex: i,
        seriesId,
        sourceMasks: masksRef.current,
        maskConfig: currentMaskDrawing
      })
    }
  }, [getAssignedSeriesId, hydrateViewerMasks, overlayScopeKey, snapshot.viewerCount])

  useEffect(() => {
    if (!snapshotToRestore || snapshotToRestore === processedSnapshotRef.current) {
      return
    }

    const currentMaskDrawing = maskDrawingRef.current
    if (!currentMaskDrawing?.projectModuleId) {
      processedSnapshotRef.current = snapshotToRestore
      onSnapshotRestored?.()
      return
    }

    for (let i = 0; i < snapshot.viewerCount; i += 1) {
      const seriesId = getAssignedSeriesId(i)
      if (!seriesId) continue
      hydrateViewerMasks({
        viewerIndex: i,
        seriesId,
        sourceMasks: snapshotToRestore.masks,
        maskConfig: currentMaskDrawing,
        syncActiveMaskInstance: i === activeViewerIndex
      })
    }

    processedSnapshotRef.current = snapshotToRestore
    onSnapshotRestored?.()
  }, [
    activeViewerIndex,
    getAssignedSeriesId,
    hydrateViewerMasks,
    onSnapshotRestored,
    snapshot.viewerCount,
    snapshotToRestore
  ])

  const {
    handleLayoutChange,
    handleCreateProtocol,
    handleEditProtocol,
    handleSetDefault,
    handleDeleteProtocol,
    isMutating
  } = buildProtocolActions({
    applyLayoutName,
    scene,
    ensureLayoutViewerCount,
    createProtocol,
    updateProtocol,
    deleteProtocol,
    selectedProtocol,
    applySelectedProtocolId
  })

  return {
    scene,
    snapshot,
    layoutName,
    activeViewerIndex,
    viewerError,
    mouseMode,
    selectedProtocolId,
    protocols,
    isLoadingProtocols,
    protocolError,
    selectedProtocol,
    isMutating,
    seriesCount: series.length,
    applyGridOpen,
    crosshairVisible,
    applyCrosshairVisible,
    applyMouseMode,
    applySelectedProtocolId,
    handleLayoutChange,
    handleCreateProtocol,
    handleEditProtocol,
    handleSetDefault,
    handleDeleteProtocol,
    applyProtocol,
    createProtocolPending: createProtocol.isPending,
    updateProtocolPending: updateProtocol.isPending,
    gridOpen,
    sliceLayoutPortalsRef,
    sliceSliderPortalsRef,
    viewerSliceLayouts,
    handleViewerSliceLayoutChange,
    samMode: maskDrawing?.samMode ?? false,
    samPending,
    samError,
    getAssignedSeriesId
  }
}
